import { api } from "../../api/client";

export type VoterScopeLevel = "district" | "constituency" | "ward" | "branch" | "pollingStation";

export interface VoterCount {
  id: string;
  scopeLevel: VoterScopeLevel;
  scopeId: string;
  period: string;
  registered: number;
  ours: number;
  sampleSize?: number | null;
  notes: string;
  source: string;
}

export interface VoterShareEntry {
  scopeLevel: VoterScopeLevel;
  scopeId: string;
  registered: number;
  ours: number;
  sampleSize?: number | null;
}

interface List<T> { items: T[]; }

export const VotersApi = {
  list: (filters?: { scopeLevel?: VoterScopeLevel; scopeId?: string; period?: string }) => {
    const params = new URLSearchParams();
    if (filters?.scopeLevel) params.set("scopeLevel", filters.scopeLevel);
    if (filters?.scopeId) params.set("scopeId", filters.scopeId);
    if (filters?.period) params.set("period", filters.period);
    const q = params.toString();
    return api<List<VoterCount>>(`/voters${q ? `?${q}` : ""}`);
  },
  get: (id: string) => api<VoterCount>(`/voters/${id}`),
  create: (body: Omit<VoterCount, "id">) =>
    api<VoterCount>("/voters", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Pick<VoterCount, "registered" | "ours" | "sampleSize" | "notes" | "source">>) =>
    api<VoterCount>(`/voters/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => api<void>(`/voters/${id}`, { method: "DELETE" }),
  shareDown: (body: { period: string; entries: VoterShareEntry[] }) =>
    api<{ ids: string[]; count: number }>(
      "/voters/share",
      { method: "POST", body: JSON.stringify(body) },
    ),
};

// --- Voter intel ----------------------------------------------------------

export type VoterIntelConfidence = "low" | "medium" | "high";
export type VoterIntelMethodology =
  | "door_to_door"
  | "rally"
  | "focus_group"
  | "phone"
  | "meeting"
  | "survey"
  | "other";

export const METHODOLOGY_LABELS: Record<VoterIntelMethodology, string> = {
  door_to_door: "Door-to-door",
  rally: "Rally",
  focus_group: "Focus group",
  phone: "Phone",
  meeting: "Community meeting",
  survey: "Written survey",
  other: "Other",
};

export interface VoterIntel {
  id: string;
  scopeLevel: "district" | "constituency" | "ward" | "branch";
  scopeId: string;
  period: string;
  // Population-level estimates (projected from the sample).
  estimatedOurs: number;
  estimatedOpposition: number;
  estimatedUndecided: number;
  // Sample-size data (raw field counts). Optional on legacy rows.
  sampleSize?: number | null;
  sampleOurs?: number | null;
  sampleOpposition?: number | null;
  sampleUndecided?: number | null;
  methodology?: VoterIntelMethodology | null;
  confidence: VoterIntelConfidence;
  raceId?: string | null;
  reasons: string;
  threats: string;
  classification: "unclassified" | "restricted" | "secret";
  // Intel quality (independent of sampleSize): how many sources fed the
  // *qualitative* assessment + 0–100 numeric confidence. Optional.
  sourcesCount?: number | null;
  confidenceScore?: number | null;
}

export const VoterIntelApi = {
  list: (filters?: { scopeLevel?: VoterIntel["scopeLevel"]; scopeId?: string; period?: string; raceId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.scopeLevel) params.set("scopeLevel", filters.scopeLevel);
    if (filters?.scopeId) params.set("scopeId", filters.scopeId);
    if (filters?.period) params.set("period", filters.period);
    if (filters?.raceId) params.set("raceId", filters.raceId);
    const q = params.toString();
    return api<List<VoterIntel>>(`/voter-intel${q ? `?${q}` : ""}`);
  },
  create: (body: Omit<VoterIntel, "id">) =>
    api<VoterIntel>("/voter-intel", { method: "POST", body: JSON.stringify(body) }),
};

// --- PVT (Parallel Vote Tabulation) ---------------------------------------

export interface PVTCandidate {
  name: string;
  party: string;
  votes: number;
}

export interface PVTResult {
  id: string;
  pollingStationId: string;
  raceId: string;
  period: string;
  candidates: PVTCandidate[];
  invalidVotes: number;
  validVotes: number;
  totalVotes: number;
  registeredVoters: number;
  turnoutPct: number;
  branchId: string | null;
  wardId: string | null;
  constituencyId: string | null;
  districtId: string | null;
  notes: string;
}

export interface PVTResultCreate {
  pollingStationId: string;
  raceId: string;
  period: string;
  candidates: PVTCandidate[];
  invalidVotes: number;
  notes: string;
}

export const PVTApi = {
  list: (filters?: { raceId?: string; period?: string; pollingStationId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.raceId) params.set("raceId", filters.raceId);
    if (filters?.period) params.set("period", filters.period);
    if (filters?.pollingStationId) params.set("pollingStationId", filters.pollingStationId);
    const q = params.toString();
    return api<List<PVTResult>>(`/pvt-results${q ? `?${q}` : ""}`);
  },
  get: (id: string) => api<PVTResult>(`/pvt-results/${id}`),
  create: (body: PVTResultCreate) =>
    api<PVTResult>("/pvt-results", { method: "POST", body: JSON.stringify(body) }),
};
